/**
 * DocxEditorView Component
 *
 * DOCX-specific editor using HTML rendering from backend.
 * Features:
 * - Display DOCX content as HTML (converted via python-mammoth)
 * - AI-powered entity detection and suggestions
 * - Text highlighting for detected entities
 * - Approve/reject suggestions workflow
 * - Export to DOCX via backend redaction
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type { HtmlContent } from '../../lib/api';
import type { EntitySuggestion } from '../../types';
import { ENTITY_TYPE_CONFIG, type EntityType } from '../../types';

interface DocxEditorViewProps {
  documentId?: string;
  htmlContent: HtmlContent;
  /** Suggestions passed from parent (already loaded at case level) */
  suggestions?: EntitySuggestion[];
  /** Selected suggestion index from parent (for navigation from ResultsPanel) */
  selectedSuggestionIndex?: number | null;
  /** Callback when user selects a suggestion */
  onSelectSuggestion?: (index: number | null) => void;
}

export function DocxEditorView({
  htmlContent,
  suggestions: propSuggestions = [],
  selectedSuggestionIndex,
  onSelectSuggestion,
}: DocxEditorViewProps) {
  const [error] = useState<string | null>(null);

  // AI Detection state - controlled by parent
  const [localSelectedSuggestion] = useState<number | null>(null);

  // Use parent's selected suggestion if provided, otherwise use local state
  const selectedSuggestion = selectedSuggestionIndex ?? localSelectedSuggestion;
  void onSelectSuggestion; // Available for future use

  // Use suggestions directly from props (controlled component)
  const suggestions = propSuggestions;

  // Ref for scrolling to highlighted text
  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll to suggestion when selectedSuggestionIndex changes from parent (ResultsPanel)
  useEffect(() => {
    if (selectedSuggestionIndex != null && contentRef.current) {
      const highlightedElement = contentRef.current.querySelector(
        `[data-suggestion-index="${selectedSuggestionIndex}"]`
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedSuggestionIndex]);

  // Helper function to apply entity highlights to HTML content
  const applyHighlights = useCallback((text: string): string => {
    if (!text || suggestions.length === 0) return text;

    let content = text;

    // Sort suggestions by text length (longest first) to avoid partial replacements
    const sortedSuggestions = [...suggestions].sort((a, b) => b.text.length - a.text.length);

    for (const suggestion of sortedSuggestions) {
      const config = ENTITY_TYPE_CONFIG[suggestion.entityType as EntityType] || ENTITY_TYPE_CONFIG.CUSTOM;

      // Determine the display style based on status
      let className = 'docx-entity-highlight';
      let displayText = suggestion.text;

      if (suggestion.status === 'approved') {
        className = 'docx-entity-redacted';
        displayText = suggestion.replacementText || '[REDACTED]';
      } else if (suggestion.status === 'rejected') {
        className = 'docx-entity-rejected';
      }

      // Escape special regex characters in the text
      const escapedText = suggestion.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      // Create highlight wrapper with data attributes for navigation
      const highlightHtml = `<span
        class="${className}"
        data-suggestion-index="${suggestion.index}"
        data-entity-type="${suggestion.entityType}"
        style="background-color: ${suggestion.status === 'approved' ? '#1f2937' : config.color}20;
               border-bottom: 2px solid ${config.color};
               padding: 0 2px;
               border-radius: 2px;
               cursor: pointer;
               ${suggestion.status === 'approved' ? 'color: #fff; background-color: #1f2937;' : ''}
               ${selectedSuggestion === suggestion.index ? 'box-shadow: 0 0 0 2px ' + config.color + ';' : ''}"
      >${displayText}</span>`;

      // Replace text occurrences (case-sensitive)
      const regex = new RegExp(escapedText, 'g');
      content = content.replace(regex, highlightHtml);
    }

    return content;
  }, [suggestions, selectedSuggestion]);

  // Process HTML content to highlight detected entities
  const processedHtmlContent = useMemo(() => {
    if (!htmlContent.html) return '';
    return applyHighlights(htmlContent.html);
  }, [htmlContent.html, applyHighlights]);

  // Calculate stats
  const stats = useMemo(() => {
    const pending = suggestions.filter(s => s.status === 'pending').length;
    const approved = suggestions.filter(s => s.status === 'approved').length;
    const rejected = suggestions.filter(s => s.status === 'rejected').length;
    return { pending, approved, rejected, total: suggestions.length };
  }, [suggestions]);

  // Get document metadata with proper typing
  const metadata = (htmlContent.metadata || {}) as {
    author?: string;
    paragraphs?: number;
    tables?: number;
    sections?: number;
    file_type?: string;
  };

  return (
    <div className="flex flex-col flex-1 bg-background h-full overflow-hidden">
      {/* Toolbar - placeholder for future editor-specific tools */}
      <div className="bg-card border-b border-border px-6 py-3">
        <div className="flex items-center justify-end flex-wrap gap-4">
          {/* Editor-specific tools can be added here */}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-destructive/10 border-b border-destructive/20 px-6 py-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex min-h-0">
        {/* Document Content Area */}
        <div className="flex-1 overflow-auto py-8 px-4 bg-[#e8e8e8] min-h-0">
          {/* Document Page - styled like a Word document page */}
          <div
            className="docx-page mx-auto bg-white"
            style={{
              width: '8.5in',
              minHeight: '11in',
              padding: '1in',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          >
            {/* Document Body */}
            <div
              ref={contentRef}
              className="docx-body-content prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: processedHtmlContent }}
            />
          </div>
        </div>

      </div>

      {/* Status Bar */}
      <div className="bg-card border-t border-border px-6 py-2 text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span>DOCX Editor</span>
            {metadata.paragraphs && (
              <>
                <span>•</span>
                <span>{metadata.paragraphs} paragraphs</span>
              </>
            )}
            {metadata.tables && metadata.tables > 0 && (
              <>
                <span>•</span>
                <span>{metadata.tables} tables</span>
              </>
            )}
            {suggestions.length > 0 && (
              <>
                <span>•</span>
                <span className="text-primary">
                  {stats.pending} AI suggestions pending
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            {stats.approved > 0 && (
              <span className="text-green-600">
                {stats.approved} redaction{stats.approved !== 1 ? 's' : ''} approved
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Styles for document content highlighting */}
      <style>{`
        .docx-page {
          /* Page styling to look like a real document */
          background: white;
          position: relative;
        }

        .docx-body-content {
          font-size: 12pt;
          line-height: 1.5;
          color: #000;
        }

        .docx-body-content h1,
        .docx-body-content h2,
        .docx-body-content h3,
        .docx-body-content h4,
        .docx-body-content h5,
        .docx-body-content h6 {
          font-family: 'Calibri', 'Arial', sans-serif;
          color: #000;
          margin-top: 1em;
          margin-bottom: 0.5em;
        }

        .docx-body-content h1 {
          font-size: 24pt;
          font-weight: bold;
        }

        .docx-body-content h2 {
          font-size: 18pt;
          font-weight: bold;
        }

        .docx-body-content h3 {
          font-size: 14pt;
          font-weight: bold;
        }

        .docx-body-content p {
          margin-bottom: 0.5em;
        }

        .docx-body-content img {
          max-width: 100%;
          height: auto;
        }

        .docx-body-content table {
          border-collapse: collapse;
          width: 100%;
          margin: 1em 0;
        }

        .docx-body-content th,
        .docx-body-content td {
          border: 1px solid #000;
          padding: 6px 10px;
          text-align: left;
        }

        .docx-body-content th {
          background-color: #f0f0f0;
          font-weight: bold;
        }

        .docx-entity-highlight {
          transition: all 0.2s ease;
        }

        .docx-entity-highlight:hover {
          filter: brightness(0.95);
        }

        .docx-entity-redacted {
          font-family: monospace;
          letter-spacing: 0.05em;
        }

        .docx-entity-rejected {
          opacity: 0.5;
          text-decoration: line-through;
        }

        /* Responsive scaling for smaller screens */
        @media screen and (max-width: 1024px) {
          .docx-page {
            width: 100% !important;
            min-height: auto !important;
            padding: 0.5in !important;
          }
        }

        @media print {
          .docx-page {
            box-shadow: none !important;
            padding: 0 !important;
          }

          .docx-entity-highlight,
          .docx-entity-redacted {
            background-color: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }

          .docx-entity-redacted {
            background-color: #000 !important;
            color: #000 !important;
          }
        }
      `}</style>
    </div>
  );
}
